/** @jsxImportSource @emotion/react */

import { memo } from "react";
import { useForm, ArrayField, UseFormMethods } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  Dialog,
  DialogContent,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  InputAdornment,
} from "@material-ui/core";

import { Button, Icon } from "..";

import {
  dialog,
  cardHeader,
  cardContent,
  section,
  inputLabel,
  domainInput,
  disabled,
  exclamationIcon,
  sectionTitle,
  list,
  listItem,
  deleteIcon,
  actions,
  actionsBorderSection,
  button,
} from "./styles";

export interface Props {
  open: boolean;
  onClose: () => void;
  fields: Partial<ArrayField<Record<string, any>>>[];
  onAdd: (domain: string) => void;
  onRemove: (index: number) => void;
  register: UseFormMethods["register"];
  editable: boolean;
}

const AllowedDomainModal = ({
  open,
  onClose,
  fields,
  onAdd,
  onRemove,
  register: propRegister,
  editable,
}: Props): JSX.Element => {
  const intl = useIntl();

  const { register, watch, setValue, setError, errors, clearErrors } = useForm();
  const allowedDomain = watch("allowedDomain", "");

  const handleCloseModal = () => {
    onClose();
  };

  const handleAdd = () => {
    try {
      // eslint-disable-next-line compat/compat
      const { origin } = new URL(allowedDomain);

      if (!fields.map((field) => field.domain).includes(origin)) {
        onAdd(origin);
        setValue("allowedDomain", "");
        clearErrors("allowedDomain");
      } else {
        setError("allowedDomain", {
          type: "duplicated",
          message: intl.formatMessage({
            id: "allowed-domain-modal.validation.duplicated-domain",
          }),
        });
      }
    } catch {
      setError("allowedDomain", {
        type: "validate",
        message: intl.formatMessage({ id: "validation.invalid-format" }),
      });
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} css={dialog} onExited={handleCloseModal} disableEnforceFocus>
      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={intl.formatMessage({
                  id: "allowed-domain-modal.title",
                })}
                css={cardHeader}
              />
              <CardContent css={cardContent}>
                <Grid container>
                  <Grid item xs={12} css={section}>
                    <FormControl fullWidth>
                      <FormLabel css={inputLabel}>
                        {intl.formatMessage({
                          id: "allowed-domain-modal.enter-domain.label",
                        })}
                      </FormLabel>
                      <TextField
                        css={domainInput}
                        name="allowedDomain"
                        placeholder={intl.formatMessage({
                          id: "allowed-domain-modal.enter-domain.placeholder",
                        })}
                        defaultValue=""
                        onChange={() => errors?.allowedDomain && clearErrors("allowedDomain")}
                        inputRef={register()}
                        error={errors?.allowedDomain}
                        helperText={errors?.allowedDomain?.message}
                        disabled={!editable}
                        {...(errors?.allowedDomain && {
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Icon name="exclamation" viewBox="0 0 48 48" css={exclamationIcon} />
                              </InputAdornment>
                            ),
                          },
                        })}
                      />
                    </FormControl>
                  </Grid>
                  {fields.length > 0 && (
                    <>
                      <Grid item xs={12} css={section}>
                        <Typography variant="body2" css={sectionTitle}>
                          {intl.formatMessage(
                            {
                              id: "allowed-domain-modal.title.count",
                            },
                            { count: fields.length }
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} css={list}>
                        {fields.map((field, idx) => (
                          <TextField
                            key={field.id}
                            fullWidth
                            name={`restrictionDomains[${idx}].domain`}
                            css={[disabled, listItem]}
                            inputRef={propRegister()}
                            inputProps={{
                              readOnly: true,
                            }}
                            // eslint-disable-next-line react/jsx-no-duplicate-props
                            InputProps={{
                              endAdornment: editable && (
                                <Icon name="delete" css={deleteIcon} onClick={() => onRemove(idx)} />
                              ),
                            }}
                            defaultValue={field.domain}
                          />
                        ))}
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
              <CardActions css={[actions, fields.length && actionsBorderSection]}>
                {allowedDomain ? (
                  <>
                    <Button variant="contained" color="white" onClick={handleCloseModal} css={button}>
                      {intl.formatMessage({ id: "btn.cancel" })}
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleAdd} css={button}>
                      {intl.formatMessage({
                        id: "allowed-domain-modal.btn.add",
                      })}
                    </Button>
                  </>
                ) : (
                  <Button variant="contained" color="primary" onClick={handleCloseModal} css={button}>
                    {intl.formatMessage({ id: "btn.done" })}
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default memo(AllowedDomainModal);
